import { reportingColors } from './colors'

export const DEFAULT_CANVAS_WIDTH = 1064
export const DEFAULT_CANVAS_HEIGHT = 364

export const DEFAULT_LIGHT_FONT = 'DM Sans'
export const DEFAULT_LABEL_FONT_SIZE = 12
export const DEFAULT_CAPTION_FONT_SIZE = 14

export enum PSVisualizationDataType {
    CURRENCY,
    PERCENT,
    INTEGER,
}

export enum VisualizationType {
    BAR_CHART,
    HEAT_MAP,
}

export interface Point {
    x: number
    y: number
}

export interface Rect {
    x: number
    y: number
    width: number
    height: number
}

export interface BoundedRect {
    left: number
    top: number
    right: number
    bottom: number
}

export enum LabelAnchorType {
    LEFT,
    TOP,
    RIGHT,
    BOTTOM,
}

export type Color = {
    red: number
    green: number
    blue: number
    alpha: number
}

export const displayLabel = (
    ctx: CanvasRenderingContext2D,
    labelText: string,
    position: Point,
    anchorType: LabelAnchorType = LabelAnchorType.LEFT,
    color: string = reportingColors.gray600
) => {
    let x = position.x
    let y = position.y
    if (anchorType === LabelAnchorType.LEFT) {
        ctx.textAlign = 'left'
        ctx.textBaseline = 'middle'
    }
    else if (anchorType === LabelAnchorType.TOP) {
        ctx.textAlign = 'center'
        ctx.textBaseline = 'top'
    }
    else if (anchorType === LabelAnchorType.RIGHT) {
        ctx.textAlign = 'right'
        ctx.textBaseline = 'middle'
    }
    else if (anchorType === LabelAnchorType.BOTTOM) {
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'
    }
    ctx.fillStyle = color
    ctx.fillText(labelText, x, y)
}

export const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    fillStyle: string,
    roundBottom: boolean = true,
    borderWidth: number = 0,
    borderColor: string = 'black'
) => {
    ctx.fillStyle = fillStyle
    ctx.beginPath()
    ctx.moveTo(x + radius, y)
    ctx.arcTo(x + width, y, x + width, y + height, radius)
    if (roundBottom) {
        ctx.arcTo(x + width, y + height, x, y + height, radius) // Bottom right corner
        ctx.arcTo(x, y + height, x, y, radius) // Bottom left corner
    }
    else {
        ctx.lineTo(x + width, y + height) // Bottom right corner (not rounded)
        ctx.lineTo(x, y + height) // Bottom left corner (not rounded)
    }
    ctx.arcTo(x, y, x + width, y, radius)
    ctx.closePath()
    ctx.fill()

    // Draw the border if borderWidth > 0
    if (borderWidth > 0) {
        ctx.strokeStyle = borderColor
        ctx.lineWidth = borderWidth
        ctx.stroke()
    }
}

export const drawRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    fillColor?: string,
    borderColor?: string,
    lineWidth: number = 0
) => {
    if (fillColor) {
        ctx.fillStyle = fillColor
    }

    if (borderColor) {
        ctx.strokeStyle = borderColor
        ctx.lineWidth = lineWidth
    }

    if (fillColor) {
        ctx.fillRect(x, y, width, height)
    }

    if (borderColor && lineWidth > 0) {
        ctx.strokeRect(x, y, width, height)
    }
}

export const parseColor = (colorString: string): Color => {
    // Function to parse hex color
    const parseHex = (hex: string): Color => {
        const r = parseInt(hex.slice(1, 3), 16)
        const g = parseInt(hex.slice(3, 5), 16)
        const b = parseInt(hex.slice(5, 7), 16)
        return { red: r, green: g, blue: b, alpha: 1 }
    }

    // Function to parse rgb color
    const parseRgb = (rgb: string): Color => {
        const match = rgb.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/)
        if (!match) {
            throw new Error('Invalid RGB format. Expected "rgb(r, g, b)"')
        }
        const [, r, g, b] = match.map(Number)
        if ([r, g, b].some((v) => v < 0 || v > 255)) {
            throw new Error('RGB values must be between 0 and 255')
        }
        return { red: r, green: g, blue: b, alpha: 1 }
    }

    // Determine format and parse accordingly
    if (colorString.startsWith('#')) {
        if (colorString.length !== 7) {
            throw new Error('Invalid hex color format. Expected "#RRGGBB"')
        }
        return parseHex(colorString)
    }
    else if (colorString.startsWith('rgb')) {
        return parseRgb(colorString)
    }
    else {
        throw new Error('Unsupported color format. Use "#RRGGBB" or "rgb(r, g, b)"')
    }
}
